import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import {
  type ActiveEntityContext,
  type Anomaly,
  type ScreenSnapshot,
  useAICopilotStore,
} from "./store";

export interface AISnapshotProvider {
  getAISnapshot: () => ScreenSnapshot | null;
}

interface UseCopilotSyncOptions {
  route?: string;
  entity?: ActiveEntityContext;
  lightweightSnapshot?: ScreenSnapshot | null;
  snapshotProviderRef?: RefObject<AISnapshotProvider | null>;
  anomalies?: Anomaly[];
  proactiveMode?: boolean;
}

function makeAnomalySignature(anomalies: Anomaly[]): string {
  return anomalies
    .map((item) => `${item.id}:${item.severity}:${item.message}:${item.contextKey ?? ""}`)
    .join("|");
}

export function useCopilotSync(options: UseCopilotSyncOptions) {
  const updateRouteContext = useAICopilotStore((state) => state.updateRouteContext);
  const updateEntityContext = useAICopilotStore((state) => state.updateEntityContext);
  const updateScreenSnapshot = useAICopilotStore((state) => state.updateScreenSnapshot);
  const setAnomalies = useAICopilotStore((state) => state.setAnomalies);
  const toggleCopilot = useAICopilotStore((state) => state.toggleCopilot);
  const isOpen = useAICopilotStore((state) => state.isOpen);

  const anomalySignature = useMemo(
    () => makeAnomalySignature(options.anomalies ?? []),
    [options.anomalies],
  );
  const previousAnomalySignature = useRef<string>("");
  const proactiveTimer = useRef<number | null>(null);

  const captureSnapshot = useCallback(
    (source: "grid_ref" | "anomaly_trigger" | "chat_submit" | "drawer_open" = "grid_ref") => {
      const snapshot = options.snapshotProviderRef?.current?.getAISnapshot() ?? null;
      if (snapshot) {
        updateScreenSnapshot(snapshot, source);
      }
      return snapshot;
    },
    [options.snapshotProviderRef, updateScreenSnapshot],
  );

  useEffect(() => {
    if (!options.route) return;
    updateRouteContext(options.route);
  }, [options.route, updateRouteContext]);

  useEffect(() => {
    const entity = options.entity;
    if (!entity) return;
    updateEntityContext(entity.type, entity.id, entity.label);
  }, [options.entity, updateEntityContext]);

  useEffect(() => {
    setAnomalies(options.anomalies ?? []);
  }, [options.anomalies, setAnomalies]);

  useEffect(() => {
    if (!options.lightweightSnapshot) return;
    updateScreenSnapshot(options.lightweightSnapshot, "route");
  }, [options.lightweightSnapshot, updateScreenSnapshot]);

  useEffect(() => {
    if (!isOpen) return;
    captureSnapshot("drawer_open");
  }, [captureSnapshot, isOpen]);

  useEffect(() => {
    if (!options.proactiveMode) {
      previousAnomalySignature.current = anomalySignature;
      return;
    }

    const hasBlockingAnomaly = (options.anomalies ?? []).some(
      (item) => item.severity === "warning" || item.severity === "critical",
    );
    const changed = previousAnomalySignature.current !== anomalySignature;
    previousAnomalySignature.current = anomalySignature;

    if (!hasBlockingAnomaly || !changed) return;

    if (proactiveTimer.current) {
      window.clearTimeout(proactiveTimer.current);
    }
    proactiveTimer.current = window.setTimeout(() => {
      captureSnapshot("anomaly_trigger");
    }, 350);

    return () => {
      if (proactiveTimer.current) {
        window.clearTimeout(proactiveTimer.current);
        proactiveTimer.current = null;
      }
    };
  }, [anomalySignature, captureSnapshot, options.anomalies, options.proactiveMode]);

  const openCopilotWithSnapshot = useCallback(() => {
    toggleCopilot(true);
    captureSnapshot("drawer_open");
  }, [captureSnapshot, toggleCopilot]);

  return {
    captureSnapshot,
    openCopilotWithSnapshot,
  };
}
